import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  UserCheck, UserX, Clock, User, Mail, Calendar,
  CheckCircle, XCircle, AlertCircle, Search, Filter,
  UserPlus, Send, Copy, Link as LinkIcon, Users
} from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useToast } from '../../hooks/use-toast';
import AdminLayout from './AdminLayout';

const AdminEleveRequests = () => {
  const [requests, setRequests] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [groupes, setGroupes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedGroupe, setSelectedGroupe] = useState('');
  const [activeTab, setActiveTab] = useState('requests'); // 'requests' or 'invite'
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteData, setInviteData] = useState({
    email: '',
    nom: '',
    prenom: '',
    role: 'eleve',
    groupe_id: ''
  });
  const [inviteLoading, setInviteLoading] = useState(false);
  const [lastInviteLink, setLastInviteLink] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [requestsRes, groupesRes, invitationsRes] = await Promise.all([
        api.get('/auth/eleve-requests'),
        api.get('/groupes'),
        api.get('/auth/pending-invitations').catch(() => ({ data: [] }))
      ]);
      setRequests(requestsRes.data);
      setGroupes(groupesRes.data);
      setInvitations(invitationsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const approveRequest = async (requestId) => {
    try {
      const params = selectedGroupe ? { groupe_id: selectedGroupe } : {};
      await api.post(`/auth/eleve-requests/${requestId}/approve`, null, { params });
      toast({ title: 'Demande approuvée', description: 'L\'élève peut maintenant accéder à son espace.' });
      setSelectedRequest(null);
      setSelectedGroupe('');
      loadData();
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible d\'approuver la demande', variant: 'destructive' });
    }
  };

  const rejectRequest = async (requestId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir rejeter cette demande ?')) return;
    try {
      await api.post(`/auth/eleve-requests/${requestId}/reject`);
      toast({ title: 'Demande rejetée' });
      loadData();
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de rejeter la demande', variant: 'destructive' });
    }
  };

  const filteredRequests = requests.filter(r => {
    if (filter === 'all') return true;
    return r.status === filter;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full flex items-center"><Clock className="h-3 w-3 mr-1" />En attente</span>;
      case 'approved':
        return <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full flex items-center"><CheckCircle className="h-3 w-3 mr-1" />Approuvé</span>;
      case 'rejected':
        return <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full flex items-center"><XCircle className="h-3 w-3 mr-1" />Rejeté</span>;
      default:
        return null;
    }
  };

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  // Invite user function
  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteData.email || !inviteData.nom || !inviteData.prenom) {
      toast({ title: 'Erreur', description: 'Veuillez remplir tous les champs obligatoires', variant: 'destructive' });
      return;
    }
    
    setInviteLoading(true);
    try {
      const response = await api.post('/auth/invite-user', inviteData);
      const fullLink = `${window.location.origin}${response.data.invitation_link}`;
      setLastInviteLink(fullLink);
      toast({ 
        title: 'Invitation envoyée !', 
        description: `Un lien d'invitation a été créé pour ${inviteData.email}` 
      });
      setInviteData({ email: '', nom: '', prenom: '', role: 'eleve', groupe_id: '' });
      loadData();
    } catch (error) {
      toast({ 
        title: 'Erreur', 
        description: error.response?.data?.detail || 'Impossible d\'envoyer l\'invitation',
        variant: 'destructive' 
      });
    } finally {
      setInviteLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({ title: 'Copié !', description: 'Le lien a été copié dans le presse-papier' });
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-eleve-requests">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-[#2C3C65]">Gestion des élèves</h1>
            {pendingCount > 0 && (
              <p className="text-[#6777A1] mt-1">{pendingCount} demande(s) en attente</p>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              onClick={async () => {
                if (!window.confirm("⚠️ Supprimer DÉFINITIVEMENT toutes les demandes en attente ET tous les comptes non approuvés (parents/élèves) ?\n\nCela revient à dire qu'aucune demande n'a jamais été faite.\nLes comptes admin/super_admin/agence ne sont PAS supprimés.\n\nAction IRRÉVERSIBLE.")) return;
                try {
                  const r = await api.delete('/auth/eleve-requests/all-pending');
                  const d = r.data || {};
                  toast({
                    title: '🗑️ Demandes supprimées',
                    description: `${d.deleted_users || 0} compte(s) + ${d.deleted_requests || 0} demande(s) + ${d.deleted_invitations || 0} invitation(s) effacés.`,
                  });
                  loadData();
                } catch (e) {
                  toast({ title: 'Erreur', description: e?.response?.data?.detail || String(e), variant: 'destructive' });
                }
              }}
              variant="outline"
              className="border-red-500 text-red-600 hover:bg-red-50"
              data-testid="delete-all-pending-btn"
            >
              🗑️ Supprimer toutes les demandes
            </Button>
            <Button
              onClick={() => setShowInviteForm(!showInviteForm)}
              className="bg-[#285BBB] hover:bg-[#2C3C65] text-white"
              data-testid="invite-user-button"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Inviter un élève
            </Button>
          </div>
        </div>

        {/* Invitation Form */}
        <AnimatePresence>
          {showInviteForm && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-white rounded-xl shadow-md p-6 overflow-hidden"
            >
              <h2 className="text-lg font-semibold text-[#2C3C65] mb-4 flex items-center">
                <UserPlus className="h-5 w-5 mr-2 text-[#285BBB]" />
                Inviter un nouvel élève
              </h2>
              <form onSubmit={handleInvite} className="space-y-4">
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#2C3C65] mb-1">Prénom *</label>
                    <Input
                      value={inviteData.prenom}
                      onChange={(e) => setInviteData({ ...inviteData, prenom: e.target.value })}
                      placeholder="Prénom de l'élève"
                      required
                      data-testid="invite-prenom"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2C3C65] mb-1">Nom *</label>
                    <Input
                      value={inviteData.nom}
                      onChange={(e) => setInviteData({ ...inviteData, nom: e.target.value })}
                      placeholder="Nom de l'élève"
                      required
                      data-testid="invite-nom"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2C3C65] mb-1">Email *</label>
                    <Input
                      type="email"
                      value={inviteData.email}
                      onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                      placeholder="email@exemple.com"
                      required
                      data-testid="invite-email"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#2C3C65] mb-1">Rôle</label>
                    <select
                      value={inviteData.role}
                      onChange={(e) => setInviteData({ ...inviteData, role: e.target.value })}
                      className="w-full rounded-md border border-gray-300 p-2"
                    >
                      <option value="eleve">Élève</option>
                      <option value="parent">Parent</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2C3C65] mb-1">Groupe (optionnel)</label>
                    <select
                      value={inviteData.groupe_id}
                      onChange={(e) => setInviteData({ ...inviteData, groupe_id: e.target.value })}
                      className="w-full rounded-md border border-gray-300 p-2"
                    >
                      <option value="">-- Aucun groupe --</option>
                      {groupes.map((g) => (
                        <option key={g.id} value={g.id}>{g.nom} ({g.niveau})</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-2">
                  <Button
                    type="submit"
                    disabled={inviteLoading}
                    className="bg-[#285BBB] hover:bg-[#2C3C65] text-white"
                    data-testid="send-invite-button"
                  >
                    {inviteLoading ? 'Envoi...' : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Envoyer l'invitation
                      </>
                    )}
                  </Button>
                  <button
                    type="button"
                    onClick={() => setShowInviteForm(false)}
                    className="text-[#6777A1] hover:text-[#2C3C65]"
                  >
                    Annuler
                  </button>
                </div>
              </form>

              {/* Last invite link */}
              {lastInviteLink && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                      <span className="text-sm text-green-700 font-medium">Lien d'invitation créé !</span>
                    </div>
                    <button
                      onClick={() => copyToClipboard(lastInviteLink)}
                      className="flex items-center text-sm text-green-700 hover:text-green-800"
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      Copier le lien
                    </button>
                  </div>
                  <p className="text-xs text-green-600 mt-2 break-all">{lastInviteLink}</p>
                  <p className="text-xs text-gray-500 mt-1">Ce lien expire dans 7 jours</p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Pending Invitations */}
        {invitations.length > 0 && (
          <div className="bg-white rounded-xl shadow-md p-4">
            <h3 className="text-sm font-semibold text-[#2C3C65] mb-3 flex items-center">
              <LinkIcon className="h-4 w-4 mr-2" />
              Invitations en attente ({invitations.filter(i => !i.expired).length})
            </h3>
            <div className="space-y-2">
              {invitations.filter(i => !i.expired).map((inv) => (
                <div key={inv.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg text-sm">
                  <div className="flex items-center">
                    <Mail className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-[#2C3C65]">{inv.email}</span>
                  </div>
                  <button
                    onClick={() => copyToClipboard(`${window.location.origin}/accept-invitation?token=${inv.token}`)}
                    className="text-[#285BBB] hover:underline text-xs"
                  >
                    Copier le lien
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex space-x-2">
          {[
            { value: 'pending', label: 'En attente', color: 'yellow' },
            { value: 'approved', label: 'Approuvées', color: 'green' },
            { value: 'rejected', label: 'Rejetées', color: 'red' },
            { value: 'all', label: 'Toutes', color: 'gray' },
          ].map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === f.value
                  ? 'bg-[#285BBB] text-white'
                  : 'bg-gray-100 text-[#6777A1] hover:bg-gray-200'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Requests List */}
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#285BBB] mx-auto"></div>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="p-12 text-center">
              <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-[#6777A1]">Aucune demande</p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredRequests.map((request) => (
                <motion.div
                  key={request.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-6 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-[#285BBB] rounded-full flex items-center justify-center text-white font-bold text-lg">
                        {request.prenom?.[0]}{request.nom?.[0]}
                      </div>
                      <div>
                        <div className="flex items-center space-x-3 flex-wrap">
                          <h3 className="font-semibold text-[#2C3C65]">
                            {request.prenom} {request.nom}
                          </h3>
                          {getStatusBadge(request.status)}
                          {request.stuck && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 border border-orange-200" title="Compte créé via inscription publique (avant correctif). Cliquez sur Approuver pour le débloquer.">
                              Compte {request.role || 'parent'} bloqué
                            </span>
                          )}
                        </div>
                        <div className="flex items-center mt-1 text-sm text-[#6777A1]">
                          <Mail className="h-4 w-4 mr-1" />
                          {request.email}
                        </div>
                        <div className="flex items-center mt-1 text-sm text-[#6777A1]">
                          <Calendar className="h-4 w-4 mr-1" />
                          Demande du {new Date(request.created_at).toLocaleDateString('fr-FR')}
                        </div>
                        {request.message && (
                          <p className="mt-2 text-sm text-[#6777A1] bg-gray-50 p-2 rounded">
                            {request.message}
                          </p>
                        )}
                      </div>
                    </div>

                    {request.status === 'pending' && (
                      <div className="flex items-center space-x-2">
                        <Button
                          onClick={() => setSelectedRequest(request)}
                          className="bg-green-500 hover:bg-green-600"
                        >
                          <UserCheck className="h-4 w-4 mr-2" />
                          Approuver
                        </Button>
                        <Button
                          onClick={() => rejectRequest(request.id)}
                          variant="outline"
                          className="text-red-500 border-red-500 hover:bg-red-50"
                        >
                          <UserX className="h-4 w-4 mr-2" />
                          Rejeter
                        </Button>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        {/* Approval Modal */}
        {selectedRequest && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedRequest(null)}>
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-xl shadow-xl w-full max-w-md p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-xl font-bold text-[#2C3C65] mb-4">Approuver la demande</h2>
              <div className="bg-[#F5F7FF] p-4 rounded-lg mb-4">
                <p className="font-medium text-[#2C3C65]">{selectedRequest.prenom} {selectedRequest.nom}</p>
                <p className="text-sm text-[#6777A1]">{selectedRequest.email}</p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-[#2C3C65] mb-2">
                  Affecter à un groupe (optionnel)
                </label>
                <select
                  value={selectedGroupe}
                  onChange={(e) => setSelectedGroupe(e.target.value)}
                  className="w-full border rounded-lg p-3"
                >
                  <option value="">-- Affecter plus tard --</option>
                  {groupes.map((groupe) => (
                    <option key={groupe.id} value={groupe.id}>
                      {groupe.nom} ({groupe.niveau})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-[#6777A1] mt-2">
                  L'élève pourra accéder au forum et aux documents de son groupe.
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <Button variant="outline" onClick={() => setSelectedRequest(null)}>
                  Annuler
                </Button>
                <Button
                  onClick={() => approveRequest(selectedRequest.id)}
                  className="bg-green-500 hover:bg-green-600"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Confirmer l'approbation
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminEleveRequests;
